import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

// Barra simples de feedback (sucesso/erro)
const FeedbackBar = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  padding: 12px 16px;
  border-radius: 8px;
  color: #fff;
  background: ${props => props.type === 'error' ? '#B00020' : props.type === 'delete' ? '#8b0000' : '#34574A'};
  box-shadow: 0 6px 24px rgba(0,0,0,0.15);
  z-index: 3000;
`;

const WelcomeHeader = styled.div`
  background: white;
  padding: 20px 40px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h1 {
    font-size: 1.5rem;
    color: #333;
    font-weight: 500;
  }
`;

const MessagesContainer = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 24px;
`;

const ContactsList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const SearchBar = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;

  input {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #ba824f;
      outline: none;
    }

    &::placeholder {
      color: #999;
    }
  }
`;

const ContactItem = styled.div`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background 0.3s ease;
  border-left: 3px solid transparent;

  ${props => props.active && `
    background: #f8f9fa;
    border-left-color: #ba824f;
  `}

  &:hover {
    background: #f8f9fa;
  }

  .avatar {
    width: 40px;
    height: 40px;
    background: #eee;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    min-width: 0;

    .name {
      font-size: 0.875rem;
      color: #333;
      margin-bottom: 4px;
      font-weight: 500;
    }

    .preview {
      font-size: 0.75rem;
      color: #666;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .phone {
      font-size: 0.8rem;
      color: #888;
      margin-top: 2px;
    }
  }

  .meta {
    text-align: right;
    flex-shrink: 0;

    .time {
      font-size: 0.75rem;
      color: #666;
      margin-bottom: 4px;
    }

    .badge {
      background: #ba824f;
      color: white;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 0.75rem;
      display: inline-block;
    }
  }
`;

const ChatContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
`;

const ChatHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 12px;

  .avatar {
    width: 48px;
    height: 48px;
    background: #eee;
    border-radius: 50%;
  }

  .info {
    .name {
      font-size: 1rem;
      color: #333;
      font-weight: 500;
      margin-bottom: 4px;
    }

    .status {
      font-size: 0.75rem;
      color: #666;
    }
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 520px;
  background: #f9fafb;
  border-radius: 10px;
`;

const Message = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'sent',
})`
  max-width: 70%;
  ${props => props.sent ? 'margin-left: auto;' : ''}

  .bubble {
    background: ${props => props.sent ? '#34574A' : '#f8f9fa'};
    color: ${props => props.sent ? 'white' : '#333'};
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 0.875rem;
  }

  .time {
    font-size: 0.75rem;
    color: #666;
    margin-top: 4px;
    text-align: ${props => props.sent ? 'right' : 'left'};
  }
`;

const ChatInput = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 12px;

  input {
    flex: 1;
    padding: 12px 16px;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 0.875rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #ba824f;
      outline: none;
    }
  }

  button {
    background: #34574A;
    color: white;
    border: none;
    padding: 0 24px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #2a4539;
    }
  }
`;

const TrashIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B00020" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="18" height="15" rx="2.5" fill="#fff"/>
    <path d="M8 6V4.5A2.5 2.5 0 0 1 10.5 2h3A2.5 2.5 0 0 1 16 4.5V6"/>
    <line x1="1" y1="6" x2="23" y2="6"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const Messages = () => {
  const [feedback, setFeedback] = useState({ open: false, type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [messages, setMessages] = useState([]); // mensagens da conversa selecionada
  const [conversations, setConversations] = useState([]); // lista de conversas
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [replyTextByConv, setReplyTextByConv] = useState({}); // conversationId -> texto
  const [legacyMessages, setLegacyMessages] = useState([]); // fallback legado
  const chatMessagesRef = useRef(null);

  const token = localStorage.getItem('auth_token');
  const decode = (jwt) => {
    try { return JSON.parse(atob(jwt.split('.')[1] || '')) || {}; } catch { return {}; }
  };
  const me = token ? decode(token) : {};
  const userId = me?.codigo;
  const roleRaw = String(me?.role || me?.papel || me?.tipo || '').toUpperCase();
  const isAdmin = ['ADMIN', 'ADM', 'MASTER', 'SUPER', 'HOST'].some(r => roleRaw.includes(r));

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const fallbackBase = 'http://192.168.15.13:8090/api';
      const base = (import.meta.env.VITE_URL_API || fallbackBase).replace(/\/$/, '');
      const url = base.includes('/api') ? `${base}/chat/conversations` : `${base}/api/chat/conversations`;

      const res = await fetch(url, {
        headers: { accept: 'application/json', Authorization: `Bearer ${token || ''}` }
      });

      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const bodyText = ct.includes('application/json') ? JSON.stringify(await res.json()) : await res.text();
        throw new Error(`Falha ao carregar conversas (${res.status}): ${bodyText}`);
      }
      if (!ct.includes('application/json')) {
        const bodyText = await res.text();
        throw new Error(`Resposta não-JSON ao carregar conversas em ${res.url || url}: ${bodyText}`);
      }

      const arr = (await res.json())?.data || [];
      if (Array.isArray(arr) && arr.length > 0) {
        setConversations(arr);
        if (!selectedConversationId) setSelectedConversationId(arr[0].id || arr[0].conversationId || null);
      } else {
        // fallback: buscar mensagens antigas do endpoint legado
        const legacyUrl = base.includes('/api') ? `${base}/mensagens` : `${base}/api/mensagens`;
        const legacyRes = await fetch(legacyUrl, {
          headers: { accept: 'application/json', Authorization: `Bearer ${token || ''}` }
        });
        const ctLegacy = legacyRes.headers.get('content-type') || '';
        if (!legacyRes.ok || !ctLegacy.includes('application/json')) {
          const bodyText = ctLegacy.includes('application/json') ? JSON.stringify(await legacyRes.json()) : await legacyRes.text();
          throw new Error(`Falha ao carregar conversas (legado) (${legacyRes.status}): ${bodyText}`);
        }
        const legacyArr = await legacyRes.json();
        const legacyList = Array.isArray(legacyArr) ? legacyArr : [];
        setLegacyMessages(legacyList);
        const conv = legacyList.length > 0 ? [{
          id: 'legacy',
          conversationId: 'legacy',
          title: 'Histórico',
          participantName: 'Legado',
          lastMessage: { content: legacyList[legacyList.length - 1]?.textoMENSAGEM || '' },
        }] : [];
        setConversations(conv);
        if (conv.length > 0) setSelectedConversationId('legacy');
      }
    } catch (e) {
      console.error(e);
      setFeedback({ open: true, type: 'error', message: 'Não foi possível carregar as conversas.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    if (!conversationId) return;
    if (conversationId === 'legacy') {
      const mapped = (legacyMessages || []).map((m, idx) => {
        const senderRaw = (m.remetenteMENSAGEM || '').toLowerCase();
        const sent = senderRaw ? !['usuario', 'cliente'].includes(senderRaw) : false;
        return {
          id: m.codigoMENSAGEM || idx,
          content: m.textoMENSAGEM,
          senderName: sent ? 'Admin' : 'Cliente',
          isMine: sent,
          direction: sent ? 'outgoing' : 'incoming',
          createdAt: m.dataMENSAGEM,
        };
      });
      setMessages(mapped);
      return;
    }
    setLoading(true);
    try {
      const fallbackBase = 'http://192.168.15.13:8090/api';
      const base = (import.meta.env.VITE_URL_API || fallbackBase).replace(/\/$/, '');
      const convId = conversationId;
      const url = base.includes('/api')
        ? `${base}/chat/conversations/${convId}/messages`
        : `${base}/api/chat/conversations/${convId}/messages`;

      const res = await fetch(url, {
        headers: { accept: 'application/json', Authorization: `Bearer ${token || ''}` }
      });

      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const bodyText = ct.includes('application/json') ? JSON.stringify(await res.json()) : await res.text();
        throw new Error(`Falha ao carregar mensagens (${res.status}): ${bodyText}`);
      }
      if (!ct.includes('application/json')) {
        const bodyText = await res.text();
        throw new Error(`Resposta não-JSON ao carregar mensagens em ${res.url || url}: ${bodyText}`);
      }

      const arr = (await res.json())?.data || [];
      setMessages(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error(e);
      setFeedback({ open: true, type: 'error', message: 'Não foi possível carregar as mensagens.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { if (selectedConversationId) fetchMessages(selectedConversationId); }, [selectedConversationId]);
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  const filteredConversations = conversations.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const lastText = typeof c.lastMessage === 'string' ? c.lastMessage : (c.lastMessage?.content || '');
    return (
      String(c.title || '').toLowerCase().includes(q) ||
      String(c.otherUser?.name || c.participantName || '').toLowerCase().includes(q) ||
      String(c.imovel?.titulo || '').toLowerCase().includes(q) ||
      String(lastText).toLowerCase().includes(q)
    );
  });

  const sendReply = async (conversationId) => {
    const texto = (replyTextByConv[conversationId] || '').trim();
    if (!texto) return;
    if (!conversationId) {
      setFeedback({ open: true, type: 'error', message: 'Selecione uma conversa para responder.' });
      return;
    }

    try {
      const fallbackBase = 'http://192.168.15.13:8090/api';
      const base = (import.meta.env.VITE_URL_API || fallbackBase).replace(/\/$/, '');
      const url = base.includes('/api') ? `${base}/chat/messages` : `${base}/api/chat/messages`;
      const body = {
        conversationId,
        content: texto,
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json', Authorization: `Bearer ${token || ''}` },
        body: JSON.stringify(body)
      });

      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        if (res.status === 400) {
          let msg = 'Falha ao enviar.';
          try {
            const j = ct.includes('application/json') ? await res.json() : await res.text();
            if (typeof j === 'string') msg = j; else msg = j?.message || j?.error || msg;
          } catch {}
          setFeedback({ open: true, type: 'error', message: msg });
          return;
        }
        const bodyText = ct.includes('application/json') ? JSON.stringify(await res.json()) : await res.text();
        throw new Error(`Falha ao enviar (${res.status}): ${bodyText}`);
      }

      setReplyTextByConv(prev => ({ ...prev, [conversationId]: '' }));
      await fetchMessages(conversationId);
    } catch (e) {
      console.error(e);
      setFeedback({ open: true, type: 'error', message: 'Não foi possível enviar a resposta.' });
    }
  };

  return (
    <PageContainer>
      <WelcomeHeader>
        <h1>Mensagens</h1>
      </WelcomeHeader>
      <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:16 }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por conversa..." style={{ flex:1, padding:10, border:'1px solid #e5e7eb', borderRadius:8 }} />
        <button onClick={fetchConversations} style={{ padding:'10px 16px', border:0, background:'#34574A', color:'#fff', borderRadius:8, fontWeight:700 }}>{loading ? 'Atualizando...' : 'Atualizar'}</button>
      </div>

      <div style={{ display:'grid', gap:16 }}>
        <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:12, padding:12 }}>
          <div style={{ fontWeight:700, marginBottom:8 }}>Conversas</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:320, overflowY:'auto' }}>
            {filteredConversations.map((c) => {
              const convId = c.id || c.conversationId;
              const active = convId === selectedConversationId;
              const participant = c.otherUser?.name || c.participantName || c.userName || c.clientName || c.cliente || 'Conversa';
              const property = c.imovel?.titulo || c.propertyTitle || c.propertyName || c.imovelTitulo || c.tituloIMOVEL || c.property?.title || c.property?.name || c.imovel?.tituloIMOVEL;
              // lastMessage from the chat API is a plain string (conteudoMENSAGEMCHAT)
              const lastText = typeof c.lastMessage === 'string' ? c.lastMessage : (c.lastMessage?.content || '');
              return (
                <div
                  key={convId}
                  onClick={() => setSelectedConversationId(convId)}
                  style={{
                    padding:10,
                    borderRadius:8,
                    border: active ? '1px solid #ba824f' : '1px solid #eee',
                    background: active ? '#f9f4ef' : '#fafafa',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontWeight:700 }}>{c.title || participant}</div>
                  {property && <div style={{ color:'#6b7280', fontSize:12 }}>{property}</div>}
                  {lastText && <div style={{ color:'#9ca3af', fontSize:12, marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{lastText}</div>}
                </div>
              );
            })}
            {filteredConversations.length === 0 && (
              <div style={{ color:'#6b7280', fontSize:13 }}>Nenhuma conversa encontrada.</div>
            )}
          </div>
        </div>

        <div style={{ background:'#fff', border:'1px solid #eee', borderRadius:12, padding:16 }}>
          <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:12 }}>
            <div style={{ fontWeight:800 }}>Mensagens</div>
            {selectedConversationId && <span style={{ color:'#6b7280', fontSize:12 }}>Conversa #{selectedConversationId}</span>}
          </div>
          <ChatMessages ref={chatMessagesRef}>
            {[...messages].sort((a,b)=> new Date(a.createdAt||0) - new Date(b.createdAt||0)).map((m, idx)=>{
              const sent = m.isMine || m.direction === 'outgoing';
              const when = m.createdAt ? new Date(m.createdAt).toLocaleString() : '—';
              const senderName = m.senderName || (sent ? 'Você' : 'Cliente');
              return (
                <Message key={m.id || idx} sent={sent}>
                  <div className="bubble">
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                      <span style={{ fontSize:11, background: sent ? '#34574A' : '#6b7280', color:'#fff', padding:'2px 8px', borderRadius:999 }}>{senderName}</span>
                      <span style={{ fontSize:12, color: sent ? 'rgba(255,255,255,0.8)' : '#6b7280', marginLeft:'auto' }}>{when}</span>
                    </div>
                    <div style={{ whiteSpace:'pre-wrap', color: sent ? '#fff' : '#333' }}>{m.content}</div>
                  </div>
                </Message>
              );
            })}
            {messages.length === 0 && (
              <div style={{ color:'#6b7280', fontSize:13 }}>Selecione uma conversa para ver mensagens.</div>
            )}
          </ChatMessages>
          <div style={{ display:'flex', gap:10, marginTop:10 }}>
            <input
              value={replyTextByConv[selectedConversationId] || ''}
              onChange={e=>setReplyTextByConv(prev=>({ ...prev, [selectedConversationId]: e.target.value }))}
              placeholder="Responder..."
              style={{ flex:1, padding:10, border:'1px solid #e5e7eb', borderRadius:8 }}
              disabled={!selectedConversationId}
            />
            <button
              onClick={()=> sendReply(selectedConversationId)}
              style={{ padding:'10px 16px', border:0, background:'#34574A', color:'#fff', borderRadius:8, fontWeight:700 }}
              disabled={!selectedConversationId}
            >
              Enviar
            </button>
          </div>
        </div>
      </div>

      {feedback.open ? (
        <FeedbackBar type={feedback.type} onClick={() => setFeedback({ open: false, type: '', message: '' })}>
          {feedback.message}
        </FeedbackBar>
      ) : null}
    </PageContainer>
  );
};

export default Messages;